import { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import DarkWebAlertsWidget from '@/components/DarkWebAlertsWidget';
import {
  Shield, Brain, AlertTriangle, Globe, Search, RefreshCw, FileText,
  Target, Zap, Loader2, CheckCircle, XCircle, Clock, Download,
  Building2, Eye, Lock, Activity, TrendingUp, BarChart3, Database,
  ExternalLink, Copy, ChevronRight, Filter, Newspaper, Bug, Fingerprint,
  Bell, Mail, Calendar, Image, Upload, Scan, Radar, Radio, Plus,
  Users, Server, Network, Hash, Link, ChevronDown, ChevronUp, Map,
  Crosshair, Layers, Send, Settings, Trash2, Edit, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, AreaChart, Area
} from 'recharts';

export default function ThreatIntelligence() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  
  // Executive Brief State
  const [briefLoading, setBriefLoading] = useState(false);
  const [currentBrief, setCurrentBrief] = useState(null);
  const [companyContext, setCompanyContext] = useState('');
  const [pastBriefs, setPastBriefs] = useState([]);
  
  // Indicator Analysis State
  const [indicatorType, setIndicatorType] = useState('ip');
  const [indicatorValue, setIndicatorValue] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Brand Protection State
  const [brandName, setBrandName] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [brandScanResult, setBrandScanResult] = useState(null);
  const [brandScanning, setBrandScanning] = useState(false);
  const [pastBrandScans, setPastBrandScans] = useState([]);
  
  // Malware Analysis State
  const [hashValue, setHashValue] = useState('');
  const [malwareResult, setMalwareResult] = useState(null);
  const [malwareAnalyzing, setMalwareAnalyzing] = useState(false);
  
  // Feeds State
  const [collectingFeeds, setCollectingFeeds] = useState(false);
  const [pulses, setPulses] = useState([]);
  const [indicators, setIndicators] = useState([]);
  
  // Alerts State
  const [alerts, setAlerts] = useState([]);
  const [alertsCount, setAlertsCount] = useState(0);
  
  // Scheduled Collection State
  const [scheduledRunning, setScheduledRunning] = useState(false);
  const [collectionHistory, setCollectionHistory] = useState([]);
  
  // Deepfake Analysis State
  const [deepfakeImage, setDeepfakeImage] = useState(null);
  const [deepfakeBrandName, setDeepfakeBrandName] = useState('');
  const [deepfakeResult, setDeepfakeResult] = useState(null);
  const [deepfakeAnalyzing, setDeepfakeAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  
  // OTX Pulse Detail State
  const [showPulseDetail, setShowPulseDetail] = useState(false);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [blockingIndicator, setBlockingIndicator] = useState(null);
  
  // AI Analysis State
  const [aiQuery, setAiQuery] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [threatCorrelation, setThreatCorrelation] = useState(null);
  const [correlating, setCorrelating] = useState(false);
  const [iocToAnalyze, setIocToAnalyze] = useState('');
  const [iocAnalysisResult, setIocAnalysisResult] = useState(null);
  const [analyzingIoc, setAnalyzingIoc] = useState(false);
  
  // Real-time Threat Stream State
  const [liveThreats, setLiveThreats] = useState([]);
  const [streamLoading, setStreamLoading] = useState(false);
  const streamIntervalRef = useRef(null);
  
  // Dark Web Monitoring State
  const [darkWebMonitoring, setDarkWebMonitoring] = useState({
    domains: [],
    emails: [],
    keywords: []
  });
  const [darkWebResults, setDarkWebResults] = useState([]);
  const [darkWebScanning, setDarkWebScanning] = useState(false);
  const [darkWebStats, setDarkWebStats] = useState({
    total_mentions: 0,
    credential_leaks: 0,
    data_breaches: 0,
    brand_mentions: 0
  });
  const [newDarkWebTarget, setNewDarkWebTarget] = useState('');
  const [darkWebTargetType, setDarkWebTargetType] = useState('domain');

  // Enterprise TIP State - IOC Management
  const [iocList, setIocList] = useState([]);
  const [iocStats, setIocStats] = useState(null);
  const [iocSearch, setIocSearch] = useState('');
  const [iocTypeFilter, setIocTypeFilter] = useState('');
  const [iocSeverityFilter, setIocSeverityFilter] = useState('');
  const [loadingIocs, setLoadingIocs] = useState(false);
  const [showAddIoc, setShowAddIoc] = useState(false);
  const [newIoc, setNewIoc] = useState({ indicator: '', indicator_type: 'ip', source: 'manual', confidence: 80, severity: 'medium', tags: '' });
  
  // Enterprise TIP State - Threat Actors
  const [threatActors, setThreatActors] = useState([]);
  const [loadingActors, setLoadingActors] = useState(false);
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorAttackMap, setActorAttackMap] = useState(null);
  const [actorSearch, setActorSearch] = useState('');
  
  // Enterprise TIP State - Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Enterprise TIP State - Feeds
  const [threatFeeds, setThreatFeeds] = useState([]);
  const [loadingFeeds, setLoadingFeeds] = useState(false);
  
  // Enterprise TIP State - Distributions
  const [distributions, setDistributions] = useState([]);
  const [loadingDistributions, setLoadingDistributions] = useState(false);
  
  // Enrichment State
  const [enrichmentResult, setEnrichmentResult] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichIndicator, setEnrichIndicator] = useState('');
  const [enrichType, setEnrichType] = useState('ip');
  
  // Dark Web Finding Detail State
  const [selectedDarkWebFinding, setSelectedDarkWebFinding] = useState(null);
  const [darkWebFindingFilter, setDarkWebFindingFilter] = useState('all'); // all, domain, email, keyword
  
  // Dark Web Real-time Alerts State
  const [darkWebAlerts, setDarkWebAlerts] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    fetchDashboard();
    fetchPastBriefs();
    fetchPulses();
    fetchIndicators();
    fetchPastBrandScans();
    fetchAlerts();
    fetchCollectionHistory();
    fetchLiveThreatStream(); // Fetch live threats on load
    fetchDarkWebMonitoring(); // Fetch dark web monitoring data
    
    // Set up polling for live threat stream
    streamIntervalRef.current = setInterval(fetchLiveThreatStream, 30000); // Every 30 seconds
    
    return () => {
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
      }
    };
  }, []);

  // WebSocket connection for real-time Dark Web alerts
  useEffect(() => {
    const connectWebSocket = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      if (!wsUrl) return;
      
      const ws = new WebSocket(`${wsUrl}/api/ws/${token}`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setWsConnected(true);
        console.log('Dark Web alerts WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'dark_web_alert') {
            // Add new alert to the list
            setDarkWebAlerts(prev => [data.alert, ...prev].slice(0, 50));
            // Also update the dark web results
            setDarkWebResults(prev => {
              const exists = prev.some(r => r.id === data.alert.id);
              if (!exists) {
                return [data.alert, ...prev];
              }
              return prev;
            });
            // Show toast notification for critical/high severity
            if (data.alert.severity === 'critical' || data.alert.severity === 'high') {
              toast.error(`Dark Web Alert: ${data.alert.title}`, {
                description: data.alert.description?.slice(0, 100) + '...'
              });
            }
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        console.log('Dark Web alerts WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/intelligence/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status');
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/intelligence/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPastBriefs = async () => {
    try {
      const response = await api.get('/intelligence/executive-briefs');
      setPastBriefs(response.data.briefs || []);
    } catch (error) {
      console.error('Failed to fetch briefs');
    }
  };

  const fetchPulses = async () => {
    try {
      // Use direct OTX feed endpoint for real-time data
      const response = await api.get('/intelligence/feeds/otx?limit=20');
      setPulses(response.data.pulses || []);
    } catch (error) {
      console.error('Failed to fetch pulses');
      // Fallback to database-stored pulses
      try {
        const fallbackResponse = await api.get('/intelligence/pulses?limit=10');
        setPulses(fallbackResponse.data.pulses || []);
      } catch (e) {
        console.error('Fallback also failed');
      }
    }
  };

  const fetchIndicators = async () => {
    try {
      const response = await api.get('/intelligence/indicators?limit=50');
      setIndicators(response.data.indicators || []);
    } catch (error) {
      console.error('Failed to fetch indicators');
    }
  };

  const fetchPastBrandScans = async () => {
    try {
      const response = await api.get('/intelligence/brand/scans');
      setPastBrandScans(response.data.scans || []);
    } catch (error) {
      console.error('Failed to fetch brand scans');
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/intelligence/alerts');
      setAlerts(response.data.alerts || []);
      setAlertsCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch alerts');
    }
  };

  const fetchCollectionHistory = async () => {
    try {
      const response = await api.get('/intelligence/collection-history');
      setCollectionHistory(response.data.collections || []);
    } catch (error) {
      console.error('Failed to fetch collection history');
    }
  };
  
  const fetchLiveThreatStream = async () => {
    try {
      setStreamLoading(true);
      const response = await api.get('/threats/stream?limit=25');
      if (response.data && response.data.threats) {
        setLiveThreats(response.data.threats);
      }
    } catch (error) {
      console.error('Failed to fetch live threat stream:', error);
    } finally {
      setStreamLoading(false);
    }
  };

  // Dark Web Monitoring Functions
  const fetchDarkWebMonitoring = async () => {
    try {
      const response = await api.get('/intelligence/dark-web/monitoring');
      if (response.data) {
        setDarkWebMonitoring(response.data.monitoring || { domains: [], emails: [], keywords: [] });
        setDarkWebResults(response.data.results || []);
        setDarkWebStats(response.data.stats || {
          total_mentions: 0,
          credential_leaks: 0,
          data_breaches: 0,
          brand_mentions: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch dark web monitoring data');
    }
  };

  const addDarkWebTarget = async () => {
    if (!newDarkWebTarget.trim()) {
      toast.error('Please enter a target to monitor');
      return;
    }
    try {
      await api.post('/intelligence/dark-web/targets', {
        target: newDarkWebTarget,
        type: darkWebTargetType
      });
      toast.success(`Added ${darkWebTargetType} to dark web monitoring`);
      setNewDarkWebTarget('');
      fetchDarkWebMonitoring();
    } catch (error) {
      toast.error('Failed to add monitoring target');
    }
  };

  const removeDarkWebTarget = async (target, type) => {
    try {
      await api.delete(`/intelligence/dark-web/targets`, { data: { target, type } });
      toast.success('Removed from monitoring');
      fetchDarkWebMonitoring();
    } catch (error) {
      toast.error('Failed to remove target');
    }
  };

  const runDarkWebScan = async () => {
    setDarkWebScanning(true);
    try {
      const response = await api.post('/intelligence/dark-web/scan');
      
      if (response.data.status === 'started') {
        // Scan started in background - poll for results
        toast.success(`Dark web scan started for ${response.data.targets_count} targets`);
        
        const scanId = response.data.scan_id;
        let pollCount = 0;
        const maxPolls = 60; // Poll for max 2 minutes
        
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await api.get(`/intelligence/dark-web/scan/${scanId}/status`);
            const scanStatus = statusRes.data;
            
            if (scanStatus.status === 'completed') {
              clearInterval(pollInterval);
              setDarkWebScanning(false);
              toast.success(`Dark web scan complete: ${scanStatus.findings_count} findings`);
              fetchDarkWebMonitoring();
            } else if (pollCount >= maxPolls) {
              clearInterval(pollInterval);
              setDarkWebScanning(false);
              toast.info(`Scan in progress: ${scanStatus.findings_count} findings so far. Refresh to see updates.`);
              fetchDarkWebMonitoring();
            } else {
              // Update progress
              if (scanStatus.findings_count > 0) {
                toast.info(`Scanning... ${scanStatus.completed_targets}/${scanStatus.targets_count} targets, ${scanStatus.findings_count} findings`, { id: 'scan-progress' });
              }
            }
            pollCount++;
          } catch (err) {
            console.error('Poll error:', err);
          }
        }, 2000); // Poll every 2 seconds
        
      } else if (response.data.status === 'no_targets') {
        toast.error('No monitoring targets configured. Add domains, emails, or keywords first.');
        setDarkWebScanning(false);
      } else {
        // Legacy response format
        toast.success(`Dark web scan complete: ${response.data.findings_count || 0} findings`);
        setDarkWebScanning(false);
        fetchDarkWebMonitoring();
      }
    } catch (error) {
      toast.error('Failed to run dark web scan');
      setDarkWebScanning(false);
    }
  };

  // ===== Dark Web Export Functions =====
  const exportDarkWebCSV = () => {
    if (!darkWebResults || darkWebResults.length === 0) {
      toast.error('No findings to export');
      return;
    }
    
    const headers = ['Title', 'Type', 'Severity', 'Target', 'Target Type', 'Source', 'Description', 'Discovered At', 'Confidence'];
    const rows = darkWebResults.map(r => [
      r.title || '',
      r.type || '',
      r.severity || '',
      r.target || '',
      r.target_type || '',
      r.source || '',
      (r.description || '').replace(/,/g, ';').replace(/\n/g, ' '),
      r.discovered_at || '',
      r.confidence || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `dark_web_findings_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Dark web findings exported to CSV');
  };

  const exportDarkWebPDF = async () => {
    if (!darkWebResults || darkWebResults.length === 0) {
      toast.error('No findings to export');
      return;
    }
    
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(139, 92, 246); // Purple
      doc.text('Dark Web Intelligence Report', 20, 20);
      
      // Date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
      
      // Summary
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Findings: ${darkWebResults.length}`, 20, 45);
      doc.text(`Critical: ${darkWebResults.filter(r => r.severity === 'critical').length}`, 20, 52);
      doc.text(`High: ${darkWebResults.filter(r => r.severity === 'high').length}`, 70, 52);
      doc.text(`Medium: ${darkWebResults.filter(r => r.severity === 'medium').length}`, 110, 52);
      
      // Findings
      let yPos = 70;
      doc.setFontSize(14);
      doc.text('Findings Detail:', 20, yPos);
      yPos += 10;
      
      darkWebResults.slice(0, 20).forEach((finding, idx) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(10);
        doc.setTextColor(finding.severity === 'critical' ? 220 : finding.severity === 'high' ? 255 : 150, 
                        finding.severity === 'critical' ? 38 : finding.severity === 'high' ? 140 : 150, 
                        38);
        doc.text(`[${(finding.severity || 'medium').toUpperCase()}]`, 20, yPos);
        
        doc.setTextColor(0, 0, 0);
        doc.text(finding.title || 'Finding', 45, yPos);
        yPos += 6;
        
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        const desc = (finding.description || '').substring(0, 100);
        doc.text(desc, 25, yPos);
        yPos += 8;
      });
      
      doc.save(`dark_web_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Dark web report exported to PDF');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Filter dark web results by target type
  const getFilteredDarkWebResults = () => {
    if (!darkWebResults) return [];
    if (darkWebFindingFilter === 'all') return darkWebResults;
    return darkWebResults.filter(r => r.target_type === darkWebFindingFilter);
  };

  // ===== Enterprise TIP Functions =====
  const fetchIocs = async () => {
    setLoadingIocs(true);
    try {
      const params = new URLSearchParams();
      if (iocSearch) params.append('search', iocSearch);
      if (iocTypeFilter && iocTypeFilter !== 'all') params.append('indicator_type', iocTypeFilter);
      if (iocSeverityFilter && iocSeverityFilter !== 'all') params.append('severity', iocSeverityFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/tip/iocs?${params.toString()}`);
      setIocList(response.data.iocs || []);
    } catch (error) {
      console.error('Failed to fetch IOCs');
      setIocList([]);
    } finally {
      setLoadingIocs(false);
    }
  };

  const fetchIocStats = async () => {
    try {
      const response = await api.get('/tip/iocs/stats');
      setIocStats(response.data);
    } catch (error) {
      console.error('Failed to fetch IOC stats');
    }
  };

  const addIoc = async () => {
    if (!newIoc.indicator.trim()) {
      toast.error('Please enter an indicator');
      return;
    }
    try {
      await api.post('/threat-intelligence', {
        ...newIoc,
        tags: newIoc.tags ? newIoc.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('IOC added successfully');
      setShowAddIoc(false);
      setNewIoc({ indicator: '', indicator_type: 'ip', source: 'manual', confidence: 80, severity: 'medium', tags: '' });
      fetchIocs();
      fetchIocStats();
    } catch (error) {
      toast.error('Failed to add IOC');
    }
  };

  const deleteIoc = async (iocId) => {
    try {
      await api.delete(`/tip/iocs/${iocId}`);
      toast.success('IOC deleted');
      fetchIocs();
      fetchIocStats();
    } catch (error) {
      toast.error('Failed to delete IOC');
    }
  };

  const fetchThreatActors = async () => {
    setLoadingActors(true);
    try {
      const params = actorSearch ? `?search=${actorSearch}` : '';
      const response = await api.get(`/tip/actors${params}`);
      setThreatActors(response.data.actors || []);
    } catch (error) {
      console.error('Failed to fetch threat actors');
      setThreatActors([]);
    } finally {
      setLoadingActors(false);
    }
  };

  const fetchActorDetail = async (actorId) => {
    try {
      const [detailRes, mapRes] = await Promise.all([
        api.get(`/tip/actors/${actorId}`),
        api.get(`/tip/actors/${actorId}/attack-map`)
      ]);
      setSelectedActor(detailRes.data);
      setActorAttackMap(mapRes.data);
    } catch (error) {
      console.error('Failed to fetch actor detail');
    }
  };

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const response = await api.get('/tip/campaigns');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns');
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const fetchThreatFeeds = async () => {
    setLoadingFeeds(true);
    try {
      const response = await api.get('/tip/feeds');
      setThreatFeeds(response.data.feeds || []);
    } catch (error) {
      console.error('Failed to fetch feeds');
      setThreatFeeds([]);
    } finally {
      setLoadingFeeds(false);
    }
  };

  const pollFeed = async (feedId) => {
    try {
      const response = await api.post(`/tip/feeds/${feedId}/poll`);
      toast.success(`Polled ${response.data.new_indicators} new indicators`);
      fetchThreatFeeds();
    } catch (error) {
      toast.error('Failed to poll feed');
    }
  };

  const fetchDistributions = async () => {
    setLoadingDistributions(true);
    try {
      const response = await api.get('/tip/distributions');
      setDistributions(response.data.distributions || []);
    } catch (error) {
      console.error('Failed to fetch distributions');
      setDistributions([]);
    } finally {
      setLoadingDistributions(false);
    }
  };

  const pushToDistribution = async (distId) => {
    try {
      const response = await api.post(`/tip/distributions/${distId}/push`, { push_all: true });
      toast.success(`Pushed ${response.data.pushed_count} IOCs`);
      fetchDistributions();
    } catch (error) {
      toast.error('Failed to push IOCs');
    }
  };

  const enrichIoc = async () => {
    if (!enrichIndicator.trim()) {
      toast.error('Please enter an indicator to enrich');
      return;
    }
    setEnriching(true);
    try {
      const response = await api.post(`/tip/enrich?indicator=${encodeURIComponent(enrichIndicator)}&indicator_type=${enrichType}`);
      setEnrichmentResult(response.data);
      toast.success('IOC enriched successfully');
    } catch (error) {
      toast.error('Failed to enrich IOC');
    } finally {
      setEnriching(false);
    }
  };

  // Load TIP data when switching tabs
  useEffect(() => {
    if (activeTab === 'ioc-management') {
      fetchIocs();
      fetchIocStats();
    } else if (activeTab === 'threat-actors') {
      fetchThreatActors();
    } else if (activeTab === 'campaigns') {
      fetchCampaigns();
    } else if (activeTab === 'feeds') {
      fetchThreatFeeds();
    } else if (activeTab === 'distributions') {
      fetchDistributions();
    }
  }, [activeTab]);

  const runScheduledCollection = async (sendAlerts = true) => {
    setScheduledRunning(true);
    try {
      const response = await api.post('/intelligence/scheduled-collection', {
        send_email_alerts: sendAlerts
      });
      toast.success(`Collection complete: ${response.data.new_threats_count} new threats found`);
      fetchAlerts();
      fetchCollectionHistory();
      fetchIndicators();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to run scheduled collection');
    } finally {
      setScheduledRunning(false);
    }
  };

  const markAlertRead = async (alertId) => {
    try {
      await api.patch(`/intelligence/alerts/${alertId}/read`);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setDeepfakeImage(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeDeepfake = async () => {
    if (!deepfakeImage || !deepfakeBrandName.trim()) {
      toast.error('Please upload an image and enter a brand name');
      return;
    }
    setDeepfakeAnalyzing(true);
    try {
      const response = await api.post('/intelligence/brand/analyze-image', {
        image_base64: deepfakeImage,
        brand_name: deepfakeBrandName
      });
      setDeepfakeResult(response.data);
      toast.success('Deepfake analysis complete');
    } catch (error) {
      toast.error('Failed to analyze image');
    } finally {
      setDeepfakeAnalyzing(false);
    }
  };

  const collectFeeds = async () => {
    setCollectingFeeds(true);
    try {
      const response = await api.post('/intelligence/collect-feeds');
      toast.success('Threat feeds collected successfully');
      fetchPulses();
      fetchIndicators();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to collect feeds');
    } finally {
      setCollectingFeeds(false);
    }
  };

  const generateBrief = async () => {
    setBriefLoading(true);
    try {
      const response = await api.post('/intelligence/executive-brief', {
        company_context: companyContext
      });
      setCurrentBrief(response.data);
      toast.success('Executive brief generated');
      fetchPastBriefs();
    } catch (error) {
      toast.error('Failed to generate brief');
    } finally {
      setBriefLoading(false);
    }
  };

  const analyzeIndicator = async () => {
    if (!indicatorValue.trim()) {
      toast.error('Please enter an indicator value');
      return;
    }
    setAnalyzing(true);
    try {
      const response = await api.post('/intelligence/analyze', {
        indicator_type: indicatorType,
        value: indicatorValue
      });
      setAnalysisResult(response.data);
      toast.success('Indicator analyzed');
    } catch (error) {
      toast.error('Failed to analyze indicator');
    } finally {
      setAnalyzing(false);
    }
  };

  const scanBrand = async () => {
    if (!brandName.trim() || !primaryDomain.trim()) {
      toast.error('Please enter brand name and domain');
      return;
    }
    setBrandScanning(true);
    try {
      const response = await api.post('/intelligence/brand/scan', {
        brand_name: brandName,
        primary_domain: primaryDomain
      });
      setBrandScanResult(response.data);
      toast.success('Brand scan completed');
      fetchPastBrandScans();
    } catch (error) {
      toast.error('Failed to scan brand');
    } finally {
      setBrandScanning(false);
    }
  };

  const analyzeMalware = async () => {
    if (!hashValue.trim()) {
      toast.error('Please enter a file hash');
      return;
    }
    setMalwareAnalyzing(true);
    try {
      const response = await api.post(`/intelligence/malware/analyze-hash?hash_value=${hashValue}`);
      setMalwareResult(response.data);
      toast.success('Malware analysis complete');
    } catch (error) {
      toast.error('Failed to analyze hash');
    } finally {
      setMalwareAnalyzing(false);
    }
  };

  // OTX Pulse Functions
  const openPulseDetail = (pulse) => {
    setSelectedPulse(pulse);
    setShowPulseDetail(true);
  };

  const handleBlockIndicator = async (indicator) => {
    setBlockingIndicator(indicator.indicator);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success(`Indicator ${indicator.indicator?.substring(0, 30)}... added to blocklist`);
    } catch (error) {
      toast.error('Failed to block indicator');
    } finally {
      setBlockingIndicator(null);
    }
  };

  const handleCreateAlert = async (indicator) => {
    toast.success(`Alert created for indicator: ${indicator.type} - ${indicator.indicator?.substring(0, 30)}...`);
  };

  const getIndicatorTypeIcon = (type) => {
    const icons = {
      'IPv4': '🌐', 'IPv6': '🌐', 'domain': '🔗', 'hostname': '🖥️',
      'URL': '🔗', 'FileHash-MD5': '📄', 'FileHash-SHA1': '📄', 'FileHash-SHA256': '📄',
      'email': '📧', 'CVE': '🛡️', 'YARA': '📜', 'Mutex': '🔒'
    };
    return icons[type] || '⚡';
  };

  // AI Analysis Functions
  const runNaturalLanguageQuery = async () => {
    if (!aiQuery.trim()) {
      toast.error('Please enter a query');
      return;
    }
    setAiAnalyzing(true);
    try {
      const response = await api.post('/intelligence/ai/query', { query: aiQuery });
      setAiResult(response.data);
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('AI analysis failed');
      setAiResult({
        query: aiQuery,
        analysis: `**Analysis unavailable**\n\nUnable to process query: "${aiQuery}"\n\n**Possible causes:**\n- AI service connectivity issue\n- API key not configured\n\nPlease check the service configuration and try again.`,
        confidence: 0,
        related_iocs: [],
        timestamp: new Date().toISOString(),
        error: true
      });
    } finally {
      setAiAnalyzing(false);
    }
  };

  const runThreatCorrelation = async () => {
    setCorrelating(true);
    try {
      const response = await api.post('/intelligence/ai/correlate');
      setThreatCorrelation(response.data);
      toast.success('Threat correlation complete');
    } catch (error) {
      toast.error('Threat correlation failed');
      setThreatCorrelation({
        correlations: [],
        summary: 'Unable to perform threat correlation. AI service unavailable.',
        timestamp: new Date().toISOString(),
        error: true
      });
    } finally {
      setCorrelating(false);
    }
  };

  const analyzeIOC = async () => {
    if (!iocToAnalyze.trim()) {
      toast.error('Please enter an IOC');
      return;
    }
    setAnalyzingIoc(true);
    try {
      const response = await api.post('/intelligence/ai/analyze-ioc', { ioc: iocToAnalyze });
      setIocAnalysisResult(response.data);
      toast.success('IOC analysis complete');
    } catch (error) {
      // Show error state instead of mock data
      const iocType = iocToAnalyze.includes('.') ? (iocToAnalyze.startsWith('http') ? 'URL' : 'domain') : 
                     iocToAnalyze.length === 32 ? 'MD5' : iocToAnalyze.length === 64 ? 'SHA256' : 'IP';
      setIocAnalysisResult({
        ioc: iocToAnalyze,
        type: iocType,
        threat_score: 0,
        first_seen: null,
        last_seen: null,
        sources_found: [],
        tags: [],
        analysis: `**Analysis unavailable for ${iocType}: ${iocToAnalyze}**\n\n` +
                  `Unable to retrieve threat intelligence data. Please check:\n` +
                  `- AI service connectivity\n` +
                  `- API key configuration\n\n` +
                  `You can manually search this IOC in external threat intelligence platforms.`,
        mitre_tactics: [],
        error: true
      });
      toast.error('IOC analysis failed - showing limited results');
    } finally {
      setAnalyzingIoc(false);
    }
  };

  const exportSTIX = async () => {
    try {
      const response = await api.get('/intelligence/export/stix');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stix-bundle-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('STIX bundle exported');
    } catch (error) {
      toast.error('Failed to export STIX');
    }
  };

  const exportPDF = async () => {
    try {
      toast.info('Generating PDF report...');
      const response = await api.get('/intelligence/export/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `threat-intelligence-report-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF report');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getRiskColor = (level) => {
    const colors = {
      critical: 'text-red-500',
      high: 'text-orange-500',
      medium: 'text-yellow-500',
      low: 'text-green-500'
    };
    return colors[level] || 'text-gray-500';
  };

  const getRiskBg = (level) => {
    const colors = {
      critical: isDarkMode ? 'bg-red-500/20' : 'bg-red-100',
      high: isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100',
      medium: isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100',
      low: isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
    };
    return colors[level] || (isDarkMode ? 'bg-gray-500/20' : 'bg-gray-100');
  };

  // Chart data
  const indicatorsByType = dashboard?.indicators_by_type 
    ? Object.entries(dashboard.indicators_by_type).map(([name, value]) => ({
        name: name.toUpperCase(),
        value,
        color: name === 'ip' ? '#ef4444' : name === 'domain' ? '#f97316' : name === 'hash' ? '#8b5cf6' : '#6366f1'
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-electric-500' : 'border-electric-600'}`}></div>
          <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading Threat Intelligence...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="threat-intelligence-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDarkMode 
              ? 'bg-violet-500/15 border border-violet-500/30' 
              : 'bg-violet-100 border border-violet-200'
          }`}>
            <Brain className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl sm:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Intelligence Engine
            </h1>
            <p className={`text-sm font-inter mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              AI-powered threat intelligence, malware analysis, and brand protection
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={collectFeeds}
            disabled={collectingFeeds}
            className={`${isDarkMode ? 'border-navy-700 hover:border-electric-500/50 hover:bg-navy-800' : 'border-slate-200 hover:border-electric-500'}`}
          >
            {collectingFeeds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Collect Feeds
          </Button>
          <Button
            variant="outline"
            onClick={exportSTIX}
            className={isDarkMode ? 'border-white/10' : 'border-slate-300'}
          >
            <Download className="w-4 h-4 mr-2" />
            Export STIX
          </Button>
          <Button
            variant="outline"
            onClick={exportPDF}
            className={isDarkMode ? 'border-white/10' : 'border-slate-300'}
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF Report
          </Button>
          <Button
            variant="outline"
            onClick={() => runScheduledCollection(true)}
            disabled={scheduledRunning}
            className={isDarkMode ? 'border-white/10' : 'border-slate-300'}
          >
            {scheduledRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Run Collection
          </Button>
          {alertsCount > 0 && (
            <Button
              variant="destructive"
              onClick={() => setActiveTab('overview')}
              className="relative"
            >
              <Bell className="w-4 h-4 mr-2" />
              Alerts
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {alertsCount}
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Configuration Status */}
      {status && (
        <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-4 flex-wrap">
            {Object.entries(status).filter(([k]) => !['help'].includes(k)).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                {value.configured ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {value.service}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className={`rounded-lg p-1 overflow-x-auto ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
          <TabsList className="inline-flex w-max gap-1 bg-transparent">
            <TabsTrigger value="overview" className="gap-1.5 whitespace-nowrap">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="ioc-management" className="gap-1.5 whitespace-nowrap">
              <Database className="w-4 h-4" />
              IOC Hub
            </TabsTrigger>
            <TabsTrigger value="threat-actors" className="gap-1.5 whitespace-nowrap">
              <Users className="w-4 h-4" />
              Actors
            </TabsTrigger>
            <TabsTrigger value="feeds" className="gap-1.5 whitespace-nowrap">
              <Radio className="w-4 h-4" />
              Feeds
            </TabsTrigger>
            <TabsTrigger value="executive-brief" className="gap-1.5 whitespace-nowrap">
              <Newspaper className="w-4 h-4" />
              Brief
            </TabsTrigger>
            <TabsTrigger value="ai-analysis" className="gap-1.5 whitespace-nowrap">
              <Brain className="w-4 h-4" />
              AI Analysis
            </TabsTrigger>
            <TabsTrigger value="malware-feed" className="gap-1.5 whitespace-nowrap">
              <Bug className="w-4 h-4" />
              Malware
            </TabsTrigger>
            <TabsTrigger value="brand-protection" className="gap-1.5 whitespace-nowrap">
              <Shield className="w-4 h-4" />
              Brand
            </TabsTrigger>
            <TabsTrigger value="dark-web" className="gap-1.5 whitespace-nowrap">
              <Eye className="w-4 h-4" />
              Dark Web
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-600/20' : 'bg-red-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {dashboard?.total_indicators || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total IOCs</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-600/20' : 'bg-orange-100'}`}>
                  <Zap className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {dashboard?.high_confidence_indicators || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>High Confidence</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-600/20' : 'bg-violet-100'}`}>
                  <Database className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {dashboard?.threat_pulses || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Threat Pulses</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-600/20' : 'bg-emerald-100'}`}>
                  <Shield className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {dashboard?.brand_scans || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Brand Scans</p>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time Threat Stream */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-600/20' : 'bg-red-100'}`}>
                  <Radio className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Real-time Threat Stream
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      LIVE
                    </span>
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Live feed from URLhaus, Feodo Tracker, ThreatFox
                  </p>
                </div>
              </div>
              <Button
                onClick={fetchLiveThreatStream}
                disabled={streamLoading}
                size="sm"
                variant="outline"
                className={isDarkMode ? 'border-white/10' : 'border-slate-200'}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${streamLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
              {liveThreats.length === 0 ? (
                <div className="text-center py-8">
                  <Radio className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
                    {streamLoading ? 'Loading live threats...' : 'No live threats detected. Click Refresh to fetch.'}
                  </p>
                </div>
              ) : (
                liveThreats.map((threat, idx) => (
                  <div 
                    key={threat.id || idx}
                    className={`p-3 rounded-lg border transition-all ${
                      threat.severity === 'critical' 
                        ? isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                        : threat.severity === 'high'
                        ? isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
                        : isDarkMode ? 'bg-zinc-800/50 border-white/10' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                            threat.severity === 'critical' 
                              ? 'bg-red-500 text-white'
                              : threat.severity === 'high'
                              ? 'bg-orange-500 text-white'
                              : isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {threat.severity}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {threat.source}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {threat.type?.replace('_', ' ')}
                          </span>
                        </div>
                        <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {threat.attack_type || threat.details}
                        </p>
                        <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {threat.details}
                        </p>
                        {threat.indicators && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {threat.indicators.ip && (
                              <code className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                                {threat.indicators.ip}
                              </code>
                            )}
                            {threat.indicators.malware && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                                {threat.indicators.malware}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={`text-xs whitespace-nowrap ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {new Date(threat.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {liveThreats.length > 0 && (
              <div className={`mt-4 pt-3 border-t flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {liveThreats.length} threats from live feeds
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveTab('malware-feed')}
                  className={isDarkMode ? 'border-white/10 text-xs' : 'border-slate-200 text-xs'}
                >
                  View Malware Analysis
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            )}
          </div>

          {/* Indicator Analysis */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Analyze Indicator
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Type</Label>
                <Select value={indicatorType} onValueChange={setIndicatorType}>
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}>
                    <SelectItem value="ip">IP Address</SelectItem>
                    <SelectItem value="domain">Domain</SelectItem>
                    <SelectItem value="md5">MD5 Hash</SelectItem>
                    <SelectItem value="sha256">SHA256 Hash</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Value</Label>
                <Input
                  value={indicatorValue}
                  onChange={(e) => setIndicatorValue(e.target.value)}
                  placeholder="e.g., 8.8.8.8 or malicious.com"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={analyzeIndicator}
                  disabled={analyzing}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Analyze
                </Button>
              </div>
            </div>

            {analysisResult && (
              <div className={`mt-4 p-4 rounded-lg ${getRiskBg(analysisResult.risk_level)}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {analysisResult.indicator}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRiskColor(analysisResult.risk_level)} ${getRiskBg(analysisResult.risk_level)}`}>
                      {analysisResult.risk_level?.toUpperCase()}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold ${getRiskColor(analysisResult.risk_level)}`}>
                    {analysisResult.risk_score}%
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  {analysisResult.sources?.otx && (
                    <div>
                      <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>AlienVault OTX</p>
                      <p className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                        {analysisResult.sources.otx.pulse_count} pulses
                      </p>
                    </div>
                  )}
                  {analysisResult.sources?.abuseipdb && (
                    <div>
                      <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>AbuseIPDB</p>
                      <p className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                        {analysisResult.sources.abuseipdb.abuse_confidence_score}% confidence, {analysisResult.sources.abuseipdb.total_reports} reports
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recent Indicators */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Recent Indicators
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {indicators.length === 0 ? (
                <p className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  No indicators collected. Click "Collect Feeds" to fetch threat data.
                </p>
              ) : (
                indicators.slice(0, 15).map((ind, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/60 hover:bg-zinc-800/60' : 'bg-slate-50 hover:bg-slate-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded font-mono ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                        {ind.type?.toUpperCase()}
                      </span>
                      <code className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        {ind.value}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${ind.confidence >= 80 ? 'text-red-500' : ind.confidence >= 50 ? 'text-yellow-500' : 'text-green-500'}`}>
                        {ind.confidence}%
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(ind.value)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* IOC Management Hub Tab */}
        <TabsContent value="ioc-management" className="space-y-6">
          {/* IOC Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Database className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total IOCs</span>
              </div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {iocStats?.total || 0}
              </p>
            </div>
            {Object.entries(iocStats?.by_type || {}).slice(0, 4).map(([type, count]) => (
              <div key={type} className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {type === 'ip' ? <Server className="w-4 h-4 text-green-500" /> :
                   type === 'domain' ? <Globe className="w-4 h-4 text-purple-500" /> :
                   type === 'url' ? <Link className="w-4 h-4 text-orange-500" /> :
                   <Hash className="w-4 h-4 text-red-500" />}
                  <span className={`text-xs capitalize ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{type}</span>
                </div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{count}</p>
              </div>
            ))}
          </div>

          {/* IOC Search & Filters */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                IOC Management Hub
              </h3>
              <Button onClick={() => setShowAddIoc(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add IOC
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search IOCs..."
                  value={iocSearch}
                  onChange={(e) => setIocSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchIocs()}
                  className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                />
              </div>
              <Select value={iocTypeFilter} onValueChange={setIocTypeFilter}>
                <SelectTrigger className={`w-[140px] ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ip">IP Address</SelectItem>
                  <SelectItem value="domain">Domain</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="hash">Hash</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
              <Select value={iocSeverityFilter} onValueChange={setIocSeverityFilter}>
                <SelectTrigger className={`w-[140px] ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchIocs} className={isDarkMode ? 'border-white/10' : ''}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>

            {/* IOC List */}
            {loadingIocs ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : iocList.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {iocList.map((ioc, idx) => (
                  <div key={ioc.id || idx} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      {ioc.indicator_type === 'ip' ? <Server className="w-4 h-4 text-green-500" /> :
                       ioc.indicator_type === 'domain' ? <Globe className="w-4 h-4 text-purple-500" /> :
                       ioc.indicator_type === 'url' ? <Link className="w-4 h-4 text-orange-500" /> :
                       <Hash className="w-4 h-4 text-red-500" />}
                      <div>
                        <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {ioc.indicator}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            ioc.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            ioc.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            ioc.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>{ioc.severity}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {ioc.source} • Confidence: {ioc.confidence}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setEnrichIndicator(ioc.indicator); setEnrichType(ioc.indicator_type); enrichIoc(); }}>
                        <Zap className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteIoc(ioc.id)} className="text-red-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No IOCs found</p>
                <p className="text-sm mt-1">Add IOCs manually or import from threat feeds</p>
              </div>
            )}
          </div>

          {/* IOC Enrichment */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              IOC Enrichment
            </h3>
            <div className="flex gap-3 mb-4">
              <Input
                placeholder="Enter indicator to enrich..."
                value={enrichIndicator}
                onChange={(e) => setEnrichIndicator(e.target.value)}
                className={`flex-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
              <Select value={enrichType} onValueChange={setEnrichType}>
                <SelectTrigger className={`w-[140px] ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ip">IP Address</SelectItem>
                  <SelectItem value="domain">Domain</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="hash">Hash</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={enrichIoc} disabled={enriching} className="bg-violet-600 hover:bg-violet-700">
                {enriching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Enrich
              </Button>
            </div>

            {enrichmentResult && (
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{enrichmentResult.indicator}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm px-3 py-1 rounded-full font-semibold ${
                      enrichmentResult.threat_score >= 80 ? 'bg-red-500/20 text-red-400' :
                      enrichmentResult.threat_score >= 60 ? 'bg-orange-500/20 text-orange-400' :
                      enrichmentResult.threat_score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      Threat Score: {enrichmentResult.threat_score}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {enrichmentResult.enrichment_sources?.map((src, idx) => (
                    <div key={idx} className={`p-2 rounded text-center ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                      <CheckCircle className="w-4 h-4 text-green-500 mx-auto mb-1" />
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{src}</span>
                    </div>
                  ))}
                </div>
                {enrichmentResult.related_actors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Related Actors: </span>
                    {enrichmentResult.related_actors.map((actor, idx) => (
                      <span key={idx} className="text-sm text-red-400 mr-2">{actor}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Threat Actors Tab */}
        <TabsContent value="threat-actors" className="space-y-6">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Threat Actor Profiles
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  APT groups and threat actor database with TTPs
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search actors..."
                  value={actorSearch}
                  onChange={(e) => setActorSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchThreatActors()}
                  className={`w-[200px] ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                />
                <Button variant="outline" onClick={fetchThreatActors} className={isDarkMode ? 'border-white/10' : ''}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {loadingActors ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {threatActors.map((actor) => (
                  <div
                    key={actor.id}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${
                      selectedActor?.actor?.id === actor.id 
                        ? isDarkMode ? 'bg-blue-500/20 border-2 border-blue-500/50' : 'bg-blue-50 border-2 border-blue-300'
                        : isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => fetchActorDetail(actor.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{actor.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            actor.motivation === 'espionage' ? 'bg-purple-500/20 text-purple-400' :
                            actor.motivation === 'financial' ? 'bg-green-500/20 text-green-400' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>{actor.motivation}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {actor.origin_country}
                          </span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        actor.sophistication === 'advanced' ? 'bg-red-500/20 text-red-400' :
                        actor.sophistication === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{actor.sophistication}</span>
                    </div>
                    <p className={`text-sm mt-2 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {actor.description}
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        <Target className="w-3 h-3 inline mr-1" />{actor.ttps?.length || 0} TTPs
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        <Database className="w-3 h-3 inline mr-1" />{actor.iocs_linked || 0} IOCs
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        <Layers className="w-3 h-3 inline mr-1" />{actor.campaigns_linked || 0} Campaigns
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actor Detail Panel */}
          {selectedActor && (
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {selectedActor.actor?.name} - MITRE ATT&CK Coverage
                </h3>
                <Button variant="outline" size="sm" onClick={() => setSelectedActor(null)} className={isDarkMode ? 'border-white/10' : ''}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              
              {actorAttackMap && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                  {Object.entries(actorAttackMap.tactics_coverage || {}).map(([tacticId, data]) => (
                    <div
                      key={tacticId}
                      className={`p-3 rounded-lg text-center ${
                        data.techniques?.length > 0
                          ? 'bg-red-500/20 border border-red-500/30'
                          : isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'
                      }`}
                    >
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {data.name}
                      </p>
                      <p className={`text-lg font-bold ${data.techniques?.length > 0 ? 'text-red-400' : isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                        {data.techniques?.length || 0}
                      </p>
                      <p className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{tacticId}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedActor.actor?.tools?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Known Tools: </span>
                  {selectedActor.actor.tools.map((tool, idx) => (
                    <span key={idx} className="text-sm text-orange-400 mr-2 px-2 py-0.5 bg-orange-500/10 rounded">{tool}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>


        {/* Feeds Tab */}
        <TabsContent value="feeds" className="space-y-6">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Threat Feeds Integration
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  STIX/TAXII feeds and threat intelligence sources
                </p>
              </div>
              <Button variant="outline" onClick={fetchThreatFeeds} className={isDarkMode ? 'border-white/10' : ''}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {loadingFeeds ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="space-y-3">
                {threatFeeds.map((feed) => (
                  <div key={feed.id} className={`p-4 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        feed.is_enabled ? 'bg-green-500/20' : 'bg-zinc-500/20'
                      }`}>
                        <Radio className={`w-5 h-5 ${feed.is_enabled ? 'text-green-400' : 'text-zinc-500'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{feed.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-600'}`}>
                            {feed.feed_type}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {feed.indicators_count?.toLocaleString()} indicators
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => pollFeed(feed.id)}
                        disabled={!feed.is_enabled}
                        className={isDarkMode ? 'border-white/10' : ''}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Poll Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>


        {/* Executive Brief Tab */}
        <TabsContent value="executive-brief" className="space-y-6">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-600/20' : 'bg-violet-100'}`}>
                <Brain className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  AI-Powered Executive Brief
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Generate strategic threat intelligence summary for leadership
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Company Context (Optional)
                </Label>
                <Textarea
                  value={companyContext}
                  onChange={(e) => setCompanyContext(e.target.value)}
                  placeholder="Describe your industry, key assets, or specific concerns..."
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                  rows={3}
                />
              </div>
              <Button
                onClick={generateBrief}
                disabled={briefLoading}
                className="bg-violet-600 hover:bg-violet-500 text-white"
              >
                {briefLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Generating Brief...
                  </>
                ) : (
                  <>
                    <Newspaper className="w-4 h-4 mr-2" />
                    Generate Executive Brief
                  </>
                )}
              </Button>
            </div>

            {currentBrief && (
              <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Generated: {new Date(currentBrief.generated_at).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(currentBrief.brief)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                  <pre className={`whitespace-pre-wrap text-sm font-sans ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    {currentBrief.brief}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Past Briefs */}
          {pastBriefs.length > 0 && (
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Past Briefs
              </h3>
              <div className="space-y-2">
                {pastBriefs.map((brief, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-zinc-900/60 hover:bg-zinc-800/60' : 'bg-slate-50 hover:bg-slate-100'}`}
                    onClick={() => setCurrentBrief(brief)}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {brief.id}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {brief.threat_count} threats analyzed
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* AI Analysis Tab */}
        <TabsContent value="ai-analysis" className="space-y-6">
          {/* Natural Language Query */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-600/20' : 'bg-purple-100'}`}>
                <Brain className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Natural Language Threat Query
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Ask questions about threats in plain English, powered by Claude Sonnet 4.5
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Input
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="e.g., Show me all ransomware IOCs from the last 7 days..."
                className={`flex-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
                onKeyDown={(e) => e.key === 'Enter' && runNaturalLanguageQuery()}
              />
              <Button
                onClick={runNaturalLanguageQuery}
                disabled={aiAnalyzing}
                className="bg-purple-600 hover:bg-purple-500 text-white"
              >
                {aiAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                Analyze
              </Button>
            </div>
            {aiResult && (
              <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    Query: "{aiResult.query}"
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                    {Math.round((aiResult.confidence || 0.85) * 100)}% confidence
                  </span>
                </div>
                <pre className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {aiResult.analysis}
                </pre>
              </div>
            )}
          </div>

          {/* IOC Analysis */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-600/20' : 'bg-blue-100'}`}>
                <Target className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Automated IOC Analysis
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Deep analysis of indicators with AI-powered threat attribution
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Input
                value={iocToAnalyze}
                onChange={(e) => setIocToAnalyze(e.target.value)}
                placeholder="Enter IP, domain, URL, or hash..."
                className={`flex-1 font-mono ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}
              />
              <Button
                onClick={analyzeIOC}
                disabled={analyzingIoc}
                className="bg-blue-600 hover:bg-blue-500 text-white"
              >
                {analyzingIoc ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                Analyze IOC
              </Button>
            </div>
            {iocAnalysisResult && (
              <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <code className={`text-sm font-mono ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      {iocAnalysisResult.ioc}
                    </code>
                    <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                      {iocAnalysisResult.type}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold ${iocAnalysisResult.threat_score >= 70 ? 'text-red-500' : iocAnalysisResult.threat_score >= 40 ? 'text-yellow-500' : 'text-green-500'}`}>
                    {iocAnalysisResult.threat_score}%
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>First Seen</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(iocAnalysisResult.first_seen).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Last Seen</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(iocAnalysisResult.last_seen).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Sources</p>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {iocAnalysisResult.sources_found?.join(', ') || 'N/A'}
                    </p>
                  </div>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>MITRE Tactics</p>
                    <p className={`text-sm font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {iocAnalysisResult.mitre_tactics?.join(', ') || 'N/A'}
                    </p>
                  </div>
                </div>
                {iocAnalysisResult.tags && iocAnalysisResult.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {iocAnalysisResult.tags.map((tag, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <pre className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {iocAnalysisResult.analysis}
                </pre>
              </div>
            )}
          </div>

          {/* Threat Correlation */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-600/20' : 'bg-orange-100'}`}>
                  <Activity className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Cross-Feed Threat Correlation
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    AI-powered analysis to find patterns across all threat intelligence sources
                  </p>
                </div>
              </div>
              <Button
                onClick={runThreatCorrelation}
                disabled={correlating}
                className="bg-orange-600 hover:bg-orange-500 text-white"
              >
                {correlating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                Run Correlation
              </Button>
            </div>
            {threatCorrelation && (
              <div className="space-y-3">
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {threatCorrelation.summary}
                </p>
                {threatCorrelation.correlations.map((corr, idx) => (
                  <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {corr.pattern}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                          {corr.indicators} IOCs
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          corr.confidence >= 0.9 ? 'bg-green-500/20 text-green-500' :
                          corr.confidence >= 0.7 ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-orange-500/20 text-orange-500'
                        }`}>
                          {Math.round(corr.confidence * 100)}% confidence
                        </span>
                      </div>
                    </div>
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {corr.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {corr.sources.map((src, sidx) => (
                        <span key={sidx} className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-200 text-slate-600'}`}>
                          {src}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Malware Feed Tab */}
        <TabsContent value="malware-feed" className="space-y-6">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-600/20' : 'bg-red-100'}`}>
                <Bug className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Malware Analysis
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Analyze file hashes using VirusTotal + AI-powered TTPs extraction
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  File Hash (MD5, SHA1, or SHA256)
                </Label>
                <Input
                  value={hashValue}
                  onChange={(e) => setHashValue(e.target.value)}
                  placeholder="Enter file hash..."
                  className={`mt-1 font-mono ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={analyzeMalware}
                  disabled={malwareAnalyzing}
                  className="w-full bg-red-600 hover:bg-red-500 text-white"
                >
                  {malwareAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                  Analyze
                </Button>
              </div>
            </div>

            {malwareResult && (
              <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <code className={`text-sm font-mono ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {malwareResult.hash}
                  </code>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    malwareResult.virustotal_data?.malicious_count > 10 
                      ? 'bg-red-500/20 text-red-500' 
                      : malwareResult.virustotal_data?.malicious_count > 0 
                        ? 'bg-yellow-500/20 text-yellow-500' 
                        : 'bg-green-500/20 text-green-500'
                  }`}>
                    {malwareResult.virustotal_data?.malicious_count || 0} detections
                  </span>
                </div>

                {malwareResult.virustotal_data && (
                  <div className={`mb-4 p-3 rounded ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      VirusTotal Results
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Type: </span>
                        <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                          {malwareResult.virustotal_data.type_description || 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Malicious: </span>
                        <span className="text-red-500">{malwareResult.virustotal_data.malicious_count}</span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Suspicious: </span>
                        <span className="text-yellow-500">{malwareResult.virustotal_data.suspicious_count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {malwareResult.ai_analysis?.analysis && (
                  <div>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      AI Analysis (Claude Sonnet 4.5)
                    </p>
                    <pre className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                      {malwareResult.ai_analysis.analysis}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Threat Pulses */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Radar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  AlienVault OTX Threat Pulses
                </h3>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                {pulses.length} pulses
              </span>
            </div>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
              Click on any pulse to view indicators and take action
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {pulses.length === 0 ? (
                <p className={`text-center py-8 col-span-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Loading pulses from AlienVault OTX...
                </p>
              ) : (
                pulses.map((pulse, idx) => (
                  <div
                    key={idx}
                    onClick={() => openPulseDetail(pulse)}
                    data-testid={`malware-pulse-${idx}`}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${isDarkMode ? 'bg-zinc-900/60 hover:bg-zinc-800 hover:border-blue-500/30 border border-transparent' : 'bg-slate-50 hover:bg-slate-100 hover:border-blue-300 border border-transparent'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`font-medium text-sm line-clamp-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {pulse.name}
                      </h4>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                          {pulse.indicator_count || 0} IOCs
                        </span>
                        <Eye className={`w-3 h-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                      </div>
                    </div>
                    <p className={`text-xs mb-2 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {pulse.description?.substring(0, 100) || 'No description'}
                    </p>
                    {pulse.malware_families && pulse.malware_families.length > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded mr-2 ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                        {pulse.malware_families[0]}
                      </span>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {pulse.tags?.slice(0, 4).map((tag, tidx) => (
                        <span
                          key={tidx}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-200 text-slate-600'}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      By {pulse.author_name || 'Unknown'} • {pulse.created ? new Date(pulse.created).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Brand Protection Tab */}
        <TabsContent value="brand-protection" className="space-y-6">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-600/20' : 'bg-emerald-100'}`}>
                <Shield className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Brand Protection Scanner
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Detect typosquatting, domain impersonation, and brand abuse
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Brand Name
                </Label>
                <Input
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g., Acme Corp"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
              </div>
              <div>
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Primary Domain
                </Label>
                <Input
                  value={primaryDomain}
                  onChange={(e) => setPrimaryDomain(e.target.value)}
                  placeholder="e.g., acme.com"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={scanBrand}
                  disabled={brandScanning}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {brandScanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
                  Scan for Threats
                </Button>
              </div>
            </div>

            {brandScanResult && (
              <div className={`mt-6 space-y-4`}>
                {/* Primary Domain Info */}
                {brandScanResult.primary_domain_info && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      Primary Domain WHOIS
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Registrar: </span>
                        <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                          {brandScanResult.primary_domain_info.registrar || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Created: </span>
                        <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                          {brandScanResult.primary_domain_info.creation_date || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Expires: </span>
                        <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                          {brandScanResult.primary_domain_info.expiration_date || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Status: </span>
                        <span className="text-emerald-500">
                          {brandScanResult.primary_domain_info.is_registered ? 'Registered' : 'Not Registered'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Typosquatting Results */}
                {brandScanResult.typosquatting && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-950/50 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        Typosquatting Scan Results
                      </p>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {brandScanResult.typosquatting.variants_checked} variants checked
                      </span>
                    </div>
                    
                    {brandScanResult.typosquatting.suspicious_variants?.length > 0 && (
                      <div className={`mb-3 p-3 rounded ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                        <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          Suspicious Domains Detected ({brandScanResult.typosquatting.suspicious_variants.length})
                        </p>
                        <div className="space-y-1">
                          {brandScanResult.typosquatting.suspicious_variants.map((v, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <code className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                                {v.domain}
                              </code>
                              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                {v.registrar}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {brandScanResult.typosquatting.registered_variants?.length > 0 && (
                      <div>
                        <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          Other Registered Variants ({brandScanResult.typosquatting.registered_variants.length})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {brandScanResult.typosquatting.registered_variants.slice(0, 10).map((v, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded font-mono ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}
                            >
                              {v.domain}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Analysis */}
                {brandScanResult.ai_analysis?.analysis && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-violet-950/30 border border-violet-500/30' : 'bg-violet-50 border border-violet-200'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>
                      <Brain className="w-3 h-3 inline mr-1" />
                      AI Threat Analysis
                    </p>
                    <pre className={`whitespace-pre-wrap text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                      {brandScanResult.ai_analysis.analysis}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Past Brand Scans */}
          {pastBrandScans.length > 0 && (
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Past Brand Scans
              </h3>
              <div className="space-y-2">
                {pastBrandScans.map((scan, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-zinc-900/60 hover:bg-zinc-800/60' : 'bg-slate-50 hover:bg-slate-100'}`}
                    onClick={() => setBrandScanResult(scan)}
                  >
                    <div>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {scan.brand_name}
                      </span>
                      <span className={`text-xs ml-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        ({scan.primary_domain})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {scan.threats?.length > 0 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-500">
                          {scan.threats.length} threats
                        </span>
                      )}
                      <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deepfake/AI Image Analysis */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-600/20' : 'bg-violet-100'}`}>
                <Scan className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Deepfake & AI Image Analysis
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Analyze images for AI manipulation, deepfakes, and brand impersonation
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Upload Image for Analysis
                </Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDarkMode 
                      ? 'border-white/10 hover:border-violet-500/50 bg-zinc-950/30' 
                      : 'border-slate-300 hover:border-violet-500 bg-slate-50'
                  }`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  {deepfakeImage ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                        Image uploaded - Click to change
                      </span>
                    </div>
                  ) : (
                    <div>
                      <Upload className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        Click to upload an image
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        PNG, JPG, WEBP up to 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    Brand Name (Context)
                  </Label>
                  <Input
                    value={deepfakeBrandName}
                    onChange={(e) => setDeepfakeBrandName(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                  />
                </div>
                <Button
                  onClick={analyzeDeepfake}
                  disabled={deepfakeAnalyzing || !deepfakeImage}
                  className="w-full bg-violet-600 hover:bg-violet-500 text-white mt-4"
                >
                  {deepfakeAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
                  Analyze for Deepfake
                </Button>
              </div>
            </div>

            {deepfakeResult && (
              <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-violet-950/30 border border-violet-500/30' : 'bg-violet-50 border border-violet-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Brain className={`w-4 h-4 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>
                    AI Analysis Results
                  </p>
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {deepfakeResult.analyzed_at}
                  </span>
                </div>
                <pre className={`whitespace-pre-wrap text-xs leading-relaxed ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {deepfakeResult.analysis}
                </pre>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Dark Web Monitoring Tab */}
        <TabsContent value="dark-web" className="space-y-6">
          {/* Dark Web Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-600/20' : 'bg-purple-100'}`}>
                  <Eye className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {darkWebStats.total_mentions}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Mentions</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-600/20' : 'bg-red-100'}`}>
                  <Lock className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {darkWebStats.credential_leaks}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Credential Leaks</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-600/20' : 'bg-orange-100'}`}>
                  <Database className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {darkWebStats.data_breaches}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Data Breaches</p>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-600/20' : 'bg-blue-100'}`}>
                  <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {darkWebStats.brand_mentions}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Brand Mentions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time Dark Web Alerts */}
          <DarkWebAlertsWidget compact={false} maxAlerts={10} />

          {/* Add Monitoring Target */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Add Monitoring Target
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={darkWebTargetType} onValueChange={setDarkWebTargetType}>
                <SelectTrigger className={`w-full sm:w-40 ${isDarkMode ? 'bg-zinc-950 border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="domain">Domain</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="keyword">Keyword</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={newDarkWebTarget}
                onChange={(e) => setNewDarkWebTarget(e.target.value)}
                placeholder={darkWebTargetType === 'domain' ? 'example.com' : darkWebTargetType === 'email' ? 'user@example.com' : 'keyword to monitor'}
                className={`flex-1 ${isDarkMode ? 'bg-zinc-950 border-white/10 text-white' : ''}`}
              />
              <Button onClick={addDarkWebTarget} className="bg-purple-600 hover:bg-purple-500">
                <Plus className="w-4 h-4 mr-2" />
                Add Target
              </Button>
              <Button 
                onClick={runDarkWebScan} 
                disabled={darkWebScanning}
                className="bg-indigo-600 hover:bg-indigo-500"
              >
                {darkWebScanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
                ) : (
                  <><Radar className="w-4 h-4 mr-2" /> Run Scan</>
                )}
              </Button>
            </div>
          </div>

          {/* Monitored Targets */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Domains */}
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Globe className="w-4 h-4 inline mr-2" />
                Monitored Domains ({darkWebMonitoring.domains?.length || 0})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {darkWebMonitoring.domains?.map((domain, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{domain}</span>
                    <Button size="sm" variant="ghost" onClick={() => removeDarkWebTarget(domain, 'domain')} className="h-6 w-6 p-0">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ))}
                {(!darkWebMonitoring.domains || darkWebMonitoring.domains.length === 0) && (
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No domains monitored</p>
                )}
              </div>
            </div>

            {/* Emails */}
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Mail className="w-4 h-4 inline mr-2" />
                Monitored Emails ({darkWebMonitoring.emails?.length || 0})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {darkWebMonitoring.emails?.map((email, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{email}</span>
                    <Button size="sm" variant="ghost" onClick={() => removeDarkWebTarget(email, 'email')} className="h-6 w-6 p-0">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ))}
                {(!darkWebMonitoring.emails || darkWebMonitoring.emails.length === 0) && (
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No emails monitored</p>
                )}
              </div>
            </div>

            {/* Keywords */}
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Search className="w-4 h-4 inline mr-2" />
                Monitored Keywords ({darkWebMonitoring.keywords?.length || 0})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {darkWebMonitoring.keywords?.map((keyword, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{keyword}</span>
                    <Button size="sm" variant="ghost" onClick={() => removeDarkWebTarget(keyword, 'keyword')} className="h-6 w-6 p-0">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ))}
                {(!darkWebMonitoring.keywords || darkWebMonitoring.keywords.length === 0) && (
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No keywords monitored</p>
                )}
              </div>
            </div>
          </div>

          {/* Dark Web Findings - Segregated by Type */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Dark Web Findings ({darkWebResults.length})
              </h3>
              <div className="flex items-center gap-2">
                {/* Filter Tabs */}
                <div className={`flex rounded-lg p-1 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'domain', label: 'Domains' },
                    { value: 'email', label: 'Emails' },
                    { value: 'keyword', label: 'Keywords' }
                  ].map(filter => (
                    <button
                      key={filter.value}
                      onClick={() => setDarkWebFindingFilter(filter.value)}
                      className={`px-3 py-1 text-xs rounded transition-all ${
                        darkWebFindingFilter === filter.value
                          ? 'bg-purple-600 text-white'
                          : isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {/* Export Buttons */}
                <Button size="sm" variant="outline" onClick={exportDarkWebCSV} className={isDarkMode ? 'border-white/10' : ''}>
                  <Download className="w-4 h-4 mr-1" />
                  CSV
                </Button>
                <Button size="sm" variant="outline" onClick={exportDarkWebPDF} className={isDarkMode ? 'border-white/10' : ''}>
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
            
            {getFilteredDarkWebResults().length > 0 ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {getFilteredDarkWebResults().map((result, idx) => (
                  <div 
                    key={result.id || idx} 
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.01] ${
                      result.severity === 'critical' ? (isDarkMode ? 'bg-red-950/30 border-red-500/30 hover:border-red-500/60' : 'bg-red-50 border-red-200 hover:border-red-400') :
                      result.severity === 'high' ? (isDarkMode ? 'bg-orange-950/30 border-orange-500/30 hover:border-orange-500/60' : 'bg-orange-50 border-orange-200 hover:border-orange-400') :
                      (isDarkMode ? 'bg-zinc-800 border-white/10 hover:border-white/30' : 'bg-slate-50 border-slate-200 hover:border-slate-400')
                    }`}
                    onClick={() => setSelectedDarkWebFinding(result)}
                    data-testid={`dark-web-finding-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            result.target_type === 'domain' ? 'bg-blue-500/20 text-blue-400' :
                            result.target_type === 'email' ? 'bg-green-500/20 text-green-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {result.target_type?.toUpperCase() || 'UNKNOWN'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            result.type === 'credential_leak' ? 'bg-red-500/20 text-red-400' :
                            result.type === 'data_breach' || result.type === 'ransomware_victim' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {result.type?.replace(/_/g, ' ').toUpperCase() || 'MENTION'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            result.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            result.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {result.severity?.toUpperCase() || 'MEDIUM'}
                          </span>
                        </div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{result.title}</p>
                        <p className={`text-sm mt-1 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{result.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            <Globe className="w-3 h-3 inline mr-1" />{result.target}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            Source: {result.source || 'Dark Web'}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {result.discovered_at ? new Date(result.discovered_at).toLocaleDateString() : 'Recently'}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No dark web findings {darkWebFindingFilter !== 'all' ? `for ${darkWebFindingFilter}s` : ''}</p>
                <p className="text-sm mt-1">Add monitoring targets and run a scan to check for exposures</p>
              </div>
            )}
          </div>

          {/* Finding Detail Modal */}
          {selectedDarkWebFinding && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedDarkWebFinding(null)}>
              <div 
                className={`w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl p-6 ${isDarkMode ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-slate-200'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Finding Details
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDarkWebFinding(null)}>
                    <XCircle className="w-5 h-5" />
                  </Button>
                </div>

                {/* Severity Banner */}
                <div className={`p-3 rounded-lg mb-4 ${
                  selectedDarkWebFinding.severity === 'critical' ? 'bg-red-500/20' :
                  selectedDarkWebFinding.severity === 'high' ? 'bg-orange-500/20' :
                  'bg-yellow-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-5 h-5 ${
                      selectedDarkWebFinding.severity === 'critical' ? 'text-red-500' :
                      selectedDarkWebFinding.severity === 'high' ? 'text-orange-500' :
                      'text-yellow-500'
                    }`} />
                    <span className={`font-semibold ${
                      selectedDarkWebFinding.severity === 'critical' ? 'text-red-500' :
                      selectedDarkWebFinding.severity === 'high' ? 'text-orange-500' :
                      'text-yellow-500'
                    }`}>
                      {selectedDarkWebFinding.severity?.toUpperCase()} SEVERITY
                    </span>
                  </div>
                </div>

                <h4 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {selectedDarkWebFinding.title}
                </h4>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target</p>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedDarkWebFinding.target}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target Type</p>
                    <p className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedDarkWebFinding.target_type}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Finding Type</p>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedDarkWebFinding.type?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Confidence</p>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{Math.round((selectedDarkWebFinding.confidence || 0.7) * 100)}%</p>
                  </div>
                </div>

                <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Source</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedDarkWebFinding.source}</p>
                </div>

                <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Description</p>
                  <p className={`${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{selectedDarkWebFinding.description}</p>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Discovered: {selectedDarkWebFinding.discovered_at ? new Date(selectedDarkWebFinding.discovered_at).toLocaleString() : 'Unknown'}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(JSON.stringify(selectedDarkWebFinding, null, 2)); toast.success('Copied to clipboard'); }}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Alerts Panel */}
      {alerts.length > 0 && (
        <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-red-950/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Bell className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
              Critical Threat Alerts
            </h3>
          </div>
          <div className="space-y-3">
            {alerts.map((alert, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-900/60 border border-red-500/20' : 'bg-white border border-red-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {alert.title}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {alert.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className={`w-3 h-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {alert.created_at}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => markAlertRead(alert.id)}
                    className={isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
                  >
                    Mark Read
                  </Button>
                </div>
                {alert.indicators?.length > 0 && (
                  <div className={`mt-3 p-2 rounded ${isDarkMode ? 'bg-zinc-950/50' : 'bg-slate-100'}`}>
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      Top Indicators:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {alert.indicators.slice(0, 5).map((ind, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-1 rounded font-mono ${isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}
                        >
                          {ind.type}: {ind.value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collection History */}
      {collectionHistory.length > 0 && (
        <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Recent Collection History
            </h3>
          </div>
          <div className="space-y-2">
            {collectionHistory.slice(0, 5).map((collection, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/60' : 'bg-slate-50'}`}
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {collection.collection_time?.split('T')[0]}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {collection.new_threats_count || 0} new threats
                  </span>
                  {collection.alerts_generated?.length > 0 && (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-500">
                      {collection.alerts_generated.length} alerts
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OTX Pulse Detail Dialog */}
      <Dialog open={showPulseDetail} onOpenChange={setShowPulseDetail}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Radar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              {selectedPulse?.name || 'Pulse Details'}
            </DialogTitle>
            {selectedPulse?.description && (
              <p className={`text-sm line-clamp-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                {selectedPulse.description}
              </p>
            )}
          </DialogHeader>
          
          {selectedPulse && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Pulse Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Author</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.author_name || 'Unknown'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Created</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.created ? new Date(selectedPulse.created).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Modified</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.modified ? new Date(selectedPulse.modified).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Total IOCs</p>
                  <p className={`font-bold text-lg ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {selectedPulse.indicator_count || selectedPulse.indicators?.length || 0}
                  </p>
                </div>
              </div>

              {/* Tags */}
              {selectedPulse.tags && selectedPulse.tags.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.tags.map((tag, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Malware Families */}
              {selectedPulse.malware_families && selectedPulse.malware_families.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Malware Families</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.malware_families.map((family, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                        {family}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Indicators List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Indicators of Compromise ({selectedPulse.indicators?.length || 0})
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const iocs = selectedPulse.indicators?.map(i => i.indicator).join('\n');
                      navigator.clipboard.writeText(iocs || '');
                      toast.success('All IOCs copied to clipboard');
                    }}
                    className={`text-xs ${isDarkMode ? 'border-zinc-700' : 'border-slate-300'}`}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy All
                  </Button>
                </div>

                {selectedPulse.indicators && selectedPulse.indicators.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {selectedPulse.indicators.map((indicator, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'} flex items-center justify-between`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getIndicatorTypeIcon(indicator.type)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                              {indicator.type}
                            </span>
                          </div>
                          <p className={`font-mono text-sm mt-1 truncate ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {indicator.indicator}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBlockIndicator(indicator)}
                            disabled={blockingIndicator === indicator.indicator}
                            className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                          >
                            {blockingIndicator === indicator.indicator ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Shield className="w-3 h-3 mr-1" />
                            )}
                            Block
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCreateAlert(indicator)}
                            className={`text-xs ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-300 text-yellow-600 hover:bg-yellow-50'}`}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Alert
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <Database className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      No indicators included in this pulse preview
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Full indicators available via OTX API
                    </p>
                  </div>
                )}
              </div>

              {/* Recommended Actions */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                <p className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  <Target className="w-4 h-4" />
                  Recommended Actions
                </p>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Review all indicators and add to blocklists as appropriate
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Search SIEM for historical matches of these IOCs
                  </li>
                  {selectedPulse.malware_families?.length > 0 && (
                    <li className="flex items-start gap-2 text-orange-400">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Known malware: Run endpoint scans for {selectedPulse.malware_families.join(', ')}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add IOC Dialog */}
      <Dialog open={showAddIoc} onOpenChange={setShowAddIoc}>
        <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-500" />
              Add New IOC
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Indicator *</Label>
              <Input
                placeholder="e.g., 192.168.1.1, evil.com, https://malware.com/payload"
                value={newIoc.indicator}
                onChange={(e) => setNewIoc(prev => ({ ...prev, indicator: e.target.value }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Type</Label>
                <Select value={newIoc.indicator_type} onValueChange={(val) => setNewIoc(prev => ({ ...prev, indicator_type: val }))}>
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ip">IP Address</SelectItem>
                    <SelectItem value="domain">Domain</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="hash">File Hash</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Severity</Label>
                <Select value={newIoc.severity} onValueChange={(val) => setNewIoc(prev => ({ ...prev, severity: val }))}>
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Source</Label>
                <Input
                  placeholder="e.g., manual, virustotal"
                  value={newIoc.source}
                  onChange={(e) => setNewIoc(prev => ({ ...prev, source: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                />
              </div>
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Confidence (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newIoc.confidence}
                  onChange={(e) => setNewIoc(prev => ({ ...prev, confidence: parseInt(e.target.value) || 0 }))}
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                />
              </div>
            </div>
            
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Tags (comma-separated)</Label>
              <Input
                placeholder="e.g., malware, apt29, ransomware"
                value={newIoc.tags}
                onChange={(e) => setNewIoc(prev => ({ ...prev, tags: e.target.value }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddIoc(false)} className={isDarkMode ? 'border-white/10' : ''}>
                Cancel
              </Button>
              <Button onClick={addIoc} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add IOC
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
